// frontend/src/pages/cargo/CertificatesList.js
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import api from './../../services/api';

// ═════════════════════════════════════════════════════════════
// CONSTANTS
// ═════════════════════════════════════════════════════════════
const PRIVILEGED_ROLES = ['underwriter', 'admin'];
const PAGE_SIZE = 25;
const AUTO_REFRESH_MS = 60_000;
const SEARCH_DEBOUNCE_MS = 250;
const SUCCESS_TOAST_MS = 3_500;
const ERROR_TOAST_MS = 6_000;
const COPIED_FLASH_MS = 1_500;
const BULK_DOWNLOAD_GAP_MS = 300;
const MAX_PAGE_BUTTONS = 5;

// Fields searchable by every user
const BASE_SEARCH_FIELDS = [
    'certificate_no',
    'idf_number',
    'quote_number',
    'transaction_no',
    'ira_policy_num',
    'issued_by_name',
    'cancelled_by_name',
];
// Extra fields searchable by underwriters / admins only
const PRIVILEGED_SEARCH_FIELDS = ['owner_name', 'owner_email', 'owner_company'];

const CSV_BASE_HEADERS = [
    'Certificate Number', 'Status', 'Policy Number', 'IDF Number', 'Quote Number',
    'Insured Amount', 'Total Charges', 'DMVIC Reference',
    'Issued On (EAT)', 'Issued By',
    'Cancelled On (EAT)', 'Cancelled By', 'Cancellation Reason',
];
const CSV_PRIVILEGED_HEADERS = ['Owner Name', 'Owner Email', 'Owner Company'];

// ═════════════════════════════════════════════════════════════
// PURE HELPERS (no component state — defined once, not per render)
// ═════════════════════════════════════════════════════════════

// Backend stores DB datetimes as "YYYY-MM-DD HH:MM:SS" in UTC.
// `new Date("2026-09-28 14:30:00")` treats that as LOCAL time,
// producing a ~3h skew in Nairobi. This normalises to UTC.
const SQL_DATETIME_RE = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;

const parseAsUTC = (val) => {
    if (!val) return null;
    try {
        const s = String(val).trim();
        if (SQL_DATETIME_RE.test(s)) return new Date(s.replace(' ', 'T') + 'Z');
        const d = new Date(s);
        return isNaN(d.getTime()) ? null : d;
    } catch {
        return null;
    }
};

const toTime = (val) => parseAsUTC(val)?.getTime() || 0;
const toAmount = (val) => Number(val || 0);
const issuedAt = (c) => c.certificate_date || c.created_at;
const isCancelledCert = (c) => !!c.cancelled_at;
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

const fieldMatches = (cert, fields, q) =>
    fields.some((f) => String(cert[f] || '').toLowerCase().includes(q));

const SORTERS = {
    newest: (a, b) => toTime(b.created_at) - toTime(a.created_at),
    oldest: (a, b) => toTime(a.created_at) - toTime(b.created_at),
    insured_high: (a, b) => toAmount(b.insured_amount) - toAmount(a.insured_amount),
    insured_low: (a, b) => toAmount(a.insured_amount) - toAmount(b.insured_amount),
};

const sortRows = (rows, sortBy) => {
    const sorted = [...rows];
    if (sortBy === 'expiry_soon') {
        const now = Date.now();
        sorted.sort(
            (a, b) => Math.abs(toTime(issuedAt(a)) - now) - Math.abs(toTime(issuedAt(b)) - now)
        );
    } else if (SORTERS[sortBy]) {
        sorted.sort(SORTERS[sortBy]);
    }
    return sorted;
};

// Triggers a browser download for a Blob
const triggerDownload = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
};

const csvEscape = (val) => {
    if (val === null || val === undefined) return '';
    const s = String(val);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const buildCsv = (rows, isPrivileged) => {
    const headers = [...CSV_BASE_HEADERS, ...(isPrivileged ? CSV_PRIVILEGED_HEADERS : [])];
    const lines = [headers.map(csvEscape).join(',')];
    for (const c of rows) {
        lines.push([
            c.certificate_no || '',
            c.cancelled_at ? 'Cancelled' : 'Issued',
            c.ira_policy_num || '',
            c.idf_number || '',
            c.quote_number || '',
            toAmount(c.insured_amount),
            Number(c.insurerDetails?.totalCharges || c.total_charges || 0),
            c.transaction_no || '',
            issuedAt(c) || '',
            c.issued_by_name || '',
            c.cancelled_at || '',
            c.cancelled_by_name || '',
            c.cancel_reason || '',
            ...(isPrivileged ? [c.owner_name || '', c.owner_email || '', c.owner_company || ''] : []),
        ].map(csvEscape).join(','));
    }
    // BOM so Excel detects UTF-8
    return '\uFEFF' + lines.join('\n');
};

// ---------- Formatters ----------
const formatDate = (val) => {
    const d = parseAsUTC(val);
    if (!d) return '-';
    try {
        const diffMs = Date.now() - d.getTime();
        if (diffMs < 0) {
            return d.toLocaleString(undefined, {
                year: 'numeric', month: 'short', day: 'numeric',
                hour: '2-digit', minute: '2-digit',
            });
        }
        const diffMins = Math.floor(diffMs / 60_000);
        const diffHours = Math.floor(diffMs / 3_600_000);
        const diffDays = Math.floor(diffMs / 86_400_000);
        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
        return String(val);
    }
};

const formatFullDate = (val) => {
    const d = parseAsUTC(val);
    if (!d) return '-';
    try {
        return d.toLocaleString(undefined, {
            year: 'numeric', month: 'short', day: '2-digit',
            hour: '2-digit', minute: '2-digit', hour12: true,
        });
    } catch {
        return String(val);
    }
};

const formatMoney = (val, currency = 'KES') => {
    if (val === null || val === undefined || val === '') return '—';
    const n = Number(val);
    if (!isFinite(n) || n === 0) return '—'; // Shows '—' instead of '$0'
    return `${currency} ${n.toLocaleString()}`; // Uses KES for Mayfair Insurance
};

// ═════════════════════════════════════════════════════════════
// HOOKS
// ═════════════════════════════════════════════════════════════

// Debounce — avoids filtering on every keystroke
function useDebounced(value, delay) {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(t);
    }, [value, delay]);
    return debounced;
}

// Tracks whether the component is still mounted (guards async setState)
function useIsMounted() {
    const ref = useRef(true);
    useEffect(() => {
        ref.current = true;
        return () => {
            ref.current = false;
        };
    }, []);
    return ref;
}

// ═════════════════════════════════════════════════════════════
// SHARED CLASS STRINGS
// ═════════════════════════════════════════════════════════════
const CONTROL_BASE =
    'bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-2xl shadow-sm';
const PAGER_BTN =
    'inline-flex items-center gap-1 px-3 py-2 rounded-xl text-sm font-semibold bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 text-gray-700 dark:text-slate-200 disabled:opacity-50 hover:border-[#3a663f] transition-all';
const SECONDARY_BTN =
    'px-4 py-2 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 rounded-xl text-sm font-semibold hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors';
const PRIMARY_GRADIENT =
    'bg-gradient-to-r from-[#2d5232] to-[#3a663f] hover:from-[#3a663f] hover:to-[#4d8c54] text-white';
const CHECKBOX =
    'rounded border-gray-300 dark:border-slate-600 text-[#3a663f] focus:ring-[#3a663f] focus:ring-2';

// ═════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═════════════════════════════════════════════════════════════
export default function CertificatesList() {
    const navigate = useNavigate();
    const { user } = useSelector((state) => state.auth);
    const isPrivileged = PRIVILEGED_ROLES.includes(user?.role);

    // ---------- Data ----------
    const [certificates, setCertificates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [lastFetched, setLastFetched] = useState(null);

    // ---------- UI state ----------
    const [filter, setFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('newest');
    const [page, setPage] = useState(1);

    const [downloadingId, setDownloadingId] = useState(null);
    const [actionError, setActionError] = useState('');
    const [actionSuccess, setActionSuccess] = useState('');
    const [copiedId, setCopiedId] = useState(null);

    // Selection (bulk actions)
    const [selectedIds, setSelectedIds] = useState(() => new Set());

    // Export dropdown
    const [exporting, setExporting] = useState(false);
    const [exportMenuOpen, setExportMenuOpen] = useState(false);

    // ---------- Refs ----------
    const exportMenuRef = useRef(null);
    const searchInputRef = useRef(null);
    const isMountedRef = useIsMounted();

    const debouncedSearch = useDebounced(searchTerm, SEARCH_DEBOUNCE_MS);

    // ---------- Toast helpers ----------
    const flashSuccess = useCallback((msg) => {
        setActionSuccess(msg);
        setTimeout(() => isMountedRef.current && setActionSuccess(''), SUCCESS_TOAST_MS);
    }, [isMountedRef]);

    const flashError = useCallback((msg) => {
        setActionError(msg);
        setTimeout(() => isMountedRef.current && setActionError(''), ERROR_TOAST_MS);
    }, [isMountedRef]);

    // ---------- Fetch ----------
    // Declared before the effects that use it, so no eslint suppressions are needed.
    const fetchCertificates = useCallback(async ({ silent = false } = {}) => {
        if (!silent) {
            setLoading(true);
            setError('');
        }
        try {
            const { data } = await api.get('/cargo/certificates');
            if (!isMountedRef.current) return;
            setCertificates(Array.isArray(data) ? data : []);
            setLastFetched(new Date());
        } catch (err) {
            console.error('[CertificatesList] Fetch error:', err);
            if (isMountedRef.current) {
                setError(err.response?.data?.error || 'Failed to load certificates. Please try again.');
            }
        } finally {
            if (isMountedRef.current && !silent) setLoading(false);
        }
    }, [isMountedRef]);

    // ---------- Effects ----------
    // Initial load
    useEffect(() => {
        fetchCertificates();
    }, [fetchCertificates]);

    // Auto-refresh every 60s, paused while the tab is hidden
    useEffect(() => {
        const id = setInterval(() => {
            if (document.visibilityState === 'visible') fetchCertificates({ silent: true });
        }, AUTO_REFRESH_MS);
        return () => clearInterval(id);
    }, [fetchCertificates]);

    // Reset to page 1 whenever filters/search/sort change
    useEffect(() => {
        setPage(1);
    }, [filter, debouncedSearch, sortBy]);

    // Close export menu on outside click
    useEffect(() => {
        const handle = (e) => {
            if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
                setExportMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handle);
        return () => document.removeEventListener('mousedown', handle);
    }, []);

    // Keyboard shortcuts: Ctrl/⌘+K focuses search, Esc clears it
    useEffect(() => {
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
                e.preventDefault();
                searchInputRef.current?.focus();
            }
            if (e.key === 'Escape' && document.activeElement === searchInputRef.current) {
                setSearchTerm('');
                searchInputRef.current?.blur();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    // ---------- Derived (single pass for counts + totals) ----------
    const { counts, stats } = useMemo(() => {
        let issued = 0;
        let totalInsured = 0;
        let activeInsured = 0;
        for (const c of certificates) {
            const amount = toAmount(c.insured_amount);
            totalInsured += amount;
            if (!isCancelledCert(c)) {
                issued += 1;
                activeInsured += amount;
            }
        }
        return {
            counts: { all: certificates.length, issued, cancelled: certificates.length - issued },
            stats: { totalInsured, activeInsured },
        };
    }, [certificates]);

    const filtered = useMemo(() => {
        let rows = certificates;

        // Status filter
        if (filter === 'issued') rows = rows.filter((c) => !isCancelledCert(c));
        else if (filter === 'cancelled') rows = rows.filter(isCancelledCert);

        // Search
        const q = debouncedSearch.trim().toLowerCase();
        if (q) {
            rows = rows.filter(
                (c) =>
                    fieldMatches(c, BASE_SEARCH_FIELDS, q) ||
                    (isPrivileged && fieldMatches(c, PRIVILEGED_SEARCH_FIELDS, q))
            );
        }

        return sortRows(rows, sortBy);
    }, [certificates, filter, debouncedSearch, sortBy, isPrivileged]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

    const paginated = useMemo(() => {
        const start = (page - 1) * PAGE_SIZE;
        return filtered.slice(start, start + PAGE_SIZE);
    }, [filtered, page]);

    // Keep page within bounds when filters shrink the list
    useEffect(() => {
        if (page > totalPages) setPage(totalPages);
    }, [page, totalPages]);

    // Windowed page numbers around the current page
    const pageNumbers = useMemo(() => {
        const start = Math.max(1, Math.min(page - 2, totalPages - (MAX_PAGE_BUTTONS - 1)));
        return Array.from({ length: Math.min(MAX_PAGE_BUTTONS, totalPages) }, (_, i) => start + i)
            .filter((p) => p <= totalPages);
    }, [page, totalPages]);

    // ---------- Selection ----------
    const toggleSelect = useCallback((id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

    const toggleSelectAll = useCallback(() => {
        setSelectedIds((prev) => {
            const allSelected =
                prev.size === paginated.length && paginated.every((c) => prev.has(c.id));
            return allSelected ? new Set() : new Set(paginated.map((c) => c.id));
        });
    }, [paginated]);

    const allOnPageSelected =
        paginated.length > 0 && paginated.every((c) => selectedIds.has(c.id));

    // ---------- Download ----------
    const downloadCertificate = useCallback(async (cert) => {
        setDownloadingId(cert.id);
        try {
            const token = localStorage.getItem('token');
            const apiBase = process.env.REACT_APP_API_URL || '/api/v1';
            const res = await fetch(`${apiBase}/cargo/certificates/${cert.id}/download`, {
                method: 'GET',
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });

            if (!res.ok) {
                let errMsg = `Download failed (${res.status})`;
                try {
                    const errJson = await res.json();
                    errMsg = errJson.error || errMsg;
                } catch { /* non-JSON error body — keep default message */ }
                throw new Error(errMsg);
            }

            const contentType = res.headers.get('content-type') || '';
            if (!contentType.includes('pdf')) throw new Error('Server did not return a PDF.');

            const blob = await res.blob();
            const magic = new TextDecoder().decode(new Uint8Array(await blob.slice(0, 4).arrayBuffer()));
            if (magic !== '%PDF') throw new Error('Downloaded file is not a valid PDF');

            const suffix = cert.cancelled_at ? '-CANCELLED' : '';
            triggerDownload(blob, `${cert.certificate_no || 'certificate'}${suffix}.pdf`);
        } catch (err) {
            console.error('[CertificatesList] Download error:', err);
            flashError(err.message || 'Could not download certificate.');
        } finally {
            if (isMountedRef.current) setDownloadingId(null);
        }
    }, [flashError, isMountedRef]);

    const bulkDownload = useCallback(async () => {
        if (selectedIds.size === 0) return;
        const selected = certificates.filter((c) => selectedIds.has(c.id));
        if (selected.length === 0) return;

        flashSuccess(`Downloading ${plural(selected.length, 'certificate')}...`);
        // Sequential with a small gap — browsers block/drop rapid parallel downloads
        for (const cert of selected) {
            // eslint-disable-next-line no-await-in-loop
            await downloadCertificate(cert);
            // eslint-disable-next-line no-await-in-loop
            await new Promise((r) => setTimeout(r, BULK_DOWNLOAD_GAP_MS));
        }
        clearSelection();
    }, [selectedIds, certificates, downloadCertificate, flashSuccess, clearSelection]);

    // ---------- Copy ----------
    const copyToClipboard = useCallback(async (text, id) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedId(id);
            setTimeout(() => isMountedRef.current && setCopiedId(null), COPIED_FLASH_MS);
        } catch {
            flashError('Could not copy to clipboard.');
        }
    }, [flashError, isMountedRef]);

    // ---------- Export ----------
    const handleExport = (scope) => {
        setExportMenuOpen(false);
        if (!isPrivileged) return;
        setExporting(true);
        try {
            const isFilteredScope = scope === 'filtered';
            const rowsToExport = isFilteredScope ? filtered : certificates;
            if (!rowsToExport.length) {
                flashError('Nothing to export.');
                return;
            }
            const csv = buildCsv(rowsToExport, isPrivileged);
            const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
            const prefix = isFilteredScope ? 'certificates-filtered' : 'certificates-all';
            triggerDownload(
                new Blob([csv], { type: 'text/csv;charset=utf-8;' }),
                `${prefix}-${stamp}.csv`
            );
            flashSuccess(`Exported ${plural(rowsToExport.length, 'certificate')}.`);
        } catch (err) {
            console.error('[CertificatesList] Export error:', err);
            flashError(err.message || 'Could not export certificates.');
        } finally {
            if (isMountedRef.current) setExporting(false);
        }
    };

    const showContent = !loading && !error;

    // =========================================================
    // RENDER
    // =========================================================
    return (
        <div className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6 animate-fadeIn">
            {/* ─── HERO HEADER ─────────────────────────────────── */}
            <div className="relative overflow-hidden bg-gradient-to-br from-[#2d5232] via-[#3a663f] to-[#4d8c54] rounded-3xl p-6 sm:p-8 shadow-2xl shadow-[#3a663f]/20">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />

                <div className="relative z-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                    <div>
                        <div className="flex items-center gap-3 mb-2">
                            <div className="w-12 h-12 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center border border-white/20">
                                <Icon name="document" className="w-6 h-6 text-white" />
                            </div>
                            <span className="text-xs font-semibold uppercase tracking-widest text-white/70">
                                Marine Cargo
                            </span>
                        </div>
                        <h1 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
                            {isPrivileged ? 'All Certificates' : 'My Certificates'}
                        </h1>
                        <p className="text-white/70 text-sm mt-2 max-w-md">
                            {isPrivileged
                                ? 'Every certificate issued across the platform — track, download, and manage them all in one place.'
                                : "Every certificate you've issued — track, download, and manage them all in one place."}
                        </p>
                        {lastFetched && (
                            <p className="text-white/40 text-[11px] mt-2 flex items-center gap-1.5">
                                <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                                Auto-refreshing · Updated {formatDate(lastFetched)}
                            </p>
                        )}
                    </div>

                    <div className="flex flex-wrap gap-3">
                        <StatCard icon="📦" label="Total" value={counts.all} highlight />
                        <StatCard icon="✅" label="Active" value={counts.issued} accent="green" />
                        <StatCard icon="❌" label="Cancelled" value={counts.cancelled} accent="red" />
                    </div>
                </div>

                {stats.totalInsured > 0 && (
                    <div className="relative z-10 mt-6 pt-5 border-t border-white/10 grid grid-cols-2 gap-4">
                        <MoneyStat label="Total Insured" value={stats.totalInsured} />
                        <MoneyStat label="Active Insured" value={stats.activeInsured} alignRight />
                    </div>
                )}
            </div>

            {/* ─── STICKY TOOLBAR ──────────────────────────────── */}
            <div className="sticky top-0 z-20 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3 bg-gray-50/90 dark:bg-slate-900/90 backdrop-blur-md border-b border-gray-100 dark:border-slate-800">
                <div className="flex flex-col lg:flex-row gap-3 items-stretch lg:items-center">
                    {/* Search */}
                    <div className="relative flex-1">
                        <Icon
                            name="search"
                            className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none"
                        />
                        <input
                            ref={searchInputRef}
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            aria-label="Search certificates"
                            placeholder={
                                isPrivileged
                                    ? 'Search by certificate, IDF, owner, policy... (⌘K)'
                                    : 'Search by certificate, IDF, policy... (⌘K)'
                            }
                            className={`w-full pl-12 pr-16 py-3 ${CONTROL_BASE} text-sm text-gray-700 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-[#3a663f] focus:border-transparent outline-none transition-all`}
                        />
                        {searchTerm && (
                            <button
                                type="button"
                                onClick={() => setSearchTerm('')}
                                aria-label="Clear search"
                                className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                            >
                                <Icon name="close" className="w-4 h-4" />
                            </button>
                        )}
                    </div>

                    {/* Status filters */}
                    <div role="group" aria-label="Filter by status" className={`flex gap-2 ${CONTROL_BASE} p-1.5`}>
                        <FilterPill label="All" count={counts.all} active={filter === 'all'} onClick={() => setFilter('all')} />
                        <FilterPill label="Active" count={counts.issued} active={filter === 'issued'} onClick={() => setFilter('issued')} accent="green" />
                        <FilterPill label="Cancelled" count={counts.cancelled} active={filter === 'cancelled'} onClick={() => setFilter('cancelled')} accent="red" />
                    </div>

                    {/* Sort */}
                    <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        aria-label="Sort certificates"
                        className={`px-4 py-3 ${CONTROL_BASE} text-sm font-semibold text-gray-700 dark:text-slate-200 hover:border-[#3a663f] dark:hover:border-green-400 focus:ring-2 focus:ring-[#3a663f] outline-none transition-all`}
                    >
                        <option value="newest">Newest first</option>
                        <option value="oldest">Oldest first</option>
                        <option value="insured_high">Highest insured</option>
                        <option value="insured_low">Lowest insured</option>
                        <option value="expiry_soon">Recently issued</option>
                    </select>

                    {/* Export (privileged only) */}
                    {isPrivileged && (
                        <div className="relative" ref={exportMenuRef}>
                            <button
                                type="button"
                                onClick={() => setExportMenuOpen((v) => !v)}
                                disabled={exporting || certificates.length === 0}
                                aria-label="Export certificates"
                                aria-haspopup="menu"
                                aria-expanded={exportMenuOpen}
                                className={`inline-flex items-center gap-2 px-4 py-3 ${CONTROL_BASE} hover:border-[#3a663f] dark:hover:border-green-400 text-gray-700 dark:text-slate-200 text-sm font-semibold transition-all disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap`}
                            >
                                {exporting ? <Spinner /> : <Icon name="folderDownload" className="w-4 h-4" />}
                                Export
                                <Icon name="chevronDown" className="w-3 h-3" strokeWidth={2.5} />
                            </button>

                            {exportMenuOpen && (
                                <div
                                    role="menu"
                                    className={`absolute right-0 mt-2 w-64 ${CONTROL_BASE} shadow-2xl z-30 overflow-hidden animate-slideDown`}
                                >
                                    <ExportMenuItem
                                        icon="filter"
                                        title="Export filtered"
                                        count={filtered.length}
                                        onClick={() => handleExport('filtered')}
                                    />
                                    <div className="border-t border-gray-100 dark:border-slate-700" />
                                    <ExportMenuItem
                                        icon="database"
                                        title="Export all"
                                        count={certificates.length}
                                        onClick={() => handleExport('all')}
                                    />
                                    <div className="px-4 py-2 bg-gray-50 dark:bg-slate-900/50 border-t border-gray-100 dark:border-slate-700">
                                        <p className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-slate-500 font-semibold">
                                            CSV · Excel-compatible
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Bulk actions bar */}
                {selectedIds.size > 0 && (
                    <div className="mt-3 flex items-center justify-between gap-3 px-4 py-2.5 rounded-2xl bg-[#3a663f] text-white shadow-lg animate-slideDown">
                        <div className="flex items-center gap-3 text-sm font-semibold">
                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/20 text-xs font-bold">
                                {selectedIds.size}
                            </span>
                            selected
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={bulkDownload}
                                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/15 hover:bg-white/25 text-sm font-semibold transition-colors"
                            >
                                <Icon name="fileDownload" className="w-4 h-4" />
                                Download all
                            </button>
                            <button
                                type="button"
                                onClick={clearSelection}
                                className="p-1.5 rounded-lg hover:bg-white/20 transition-colors"
                                aria-label="Clear selection"
                            >
                                <Icon name="close" className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* ─── TOASTS ─────────────────────────────────────── */}
            {actionSuccess && <Toast variant="success" message={actionSuccess} />}
            {actionError && (
                <Toast variant="error" message={actionError} onDismiss={() => setActionError('')} />
            )}

            {/* ─── LOADING SKELETON ──────────────────────────── */}
            {loading && <LoadingSkeleton />}

            {/* ─── ERROR STATE ──────────────────────────────── */}
            {!loading && error && <ErrorState message={error} onRetry={() => fetchCertificates()} />}

            {/* ─── EMPTY ───────────────────────────────────── */}
            {showContent && filtered.length === 0 && (
                <EmptyState
                    filter={filter}
                    searchTerm={searchTerm}
                    hasAny={certificates.length > 0}
                    isPrivileged={isPrivileged}
                    onClearSearch={() => setSearchTerm('')}
                    onClearFilter={() => setFilter('all')}
                    onGoToCargo={() => navigate('/cargo')}
                />
            )}

            {/* ─── LIST ─────────────────────────────────────── */}
            {showContent && paginated.length > 0 && (
                <div className="space-y-4">
                    <div className="flex items-center justify-between px-1">
                        <label className="inline-flex items-center gap-2 cursor-pointer select-none text-sm text-gray-500 dark:text-slate-400">
                            <input
                                type="checkbox"
                                checked={allOnPageSelected}
                                onChange={toggleSelectAll}
                                className={`w-4 h-4 ${CHECKBOX}`}
                            />
                            Select all on this page
                        </label>
                        <p className="text-xs text-gray-400 dark:text-slate-500" aria-live="polite">
                            Showing {paginated.length} of {filtered.length}
                        </p>
                    </div>

                    {paginated.map((cert) => (
                        <CertificateRow
                            key={cert.id}
                            cert={cert}
                            isPrivileged={isPrivileged}
                            selected={selectedIds.has(cert.id)}
                            onToggleSelect={() => toggleSelect(cert.id)}
                            onDownload={() => downloadCertificate(cert)}
                            downloading={downloadingId === cert.id}
                            onView={() => navigate(`/cargo/certificates/${cert.id}`)}
                            onCopy={() => copyToClipboard(cert.certificate_no, cert.id)}
                            copied={copiedId === cert.id}
                        />
                    ))}
                </div>
            )}

            {/* ─── PAGINATION ───────────────────────────────── */}
            {showContent && totalPages > 1 && (
                <nav aria-label="Pagination" className="flex items-center justify-center gap-2 pt-2">
                    <button
                        type="button"
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page === 1}
                        className={PAGER_BTN}
                    >
                        <Icon name="chevronLeft" className="w-4 h-4" />
                        Prev
                    </button>
                    <div className="flex items-center gap-1">
                        {pageNumbers.map((p) => (
                            <button
                                type="button"
                                key={p}
                                onClick={() => setPage(p)}
                                aria-current={p === page ? 'page' : undefined}
                                className={`w-9 h-9 rounded-xl text-sm font-bold transition-all ${p === page
                                    ? 'bg-[#3a663f] text-white shadow-md'
                                    : 'bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-300 hover:border-[#3a663f]'
                                    }`}
                            >
                                {p}
                            </button>
                        ))}
                    </div>
                    <button
                        type="button"
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                        className={PAGER_BTN}
                    >
                        Next
                        <Icon name="chevronRight" className="w-4 h-4" />
                    </button>
                </nav>
            )}

            <style>{`
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                @keyframes slideDown { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }
                .animate-fadeIn { animation: fadeIn 0.25s ease-out; }
                .animate-slideDown { animation: slideDown 0.2s ease-out; }
                @media (prefers-reduced-motion: reduce) {
                    .animate-fadeIn, .animate-slideDown { animation: none; }
                }
            `}</style>
        </div>
    );
}

// ═════════════════════════════════════════════════════════════
// ICONS
// ═════════════════════════════════════════════════════════════
const ICON_PATHS = {
    document: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
    fileDownload: 'M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
    folderDownload: 'M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z',
    search: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z',
    close: 'M6 18L18 6M6 6l12 12',
    check: 'M5 13l4 4L19 7',
    checkCircle: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
    alertCircle: 'M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
    chevronDown: 'M19 9l-7 7-7-7',
    chevronLeft: 'M15 19l-7-7 7-7',
    chevronRight: 'M9 5l7 7-7 7',
    filter: 'M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z',
    database: 'M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4',
    copy: 'M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z',
    tag: 'M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z',
    eye: [
        'M15 12a3 3 0 11-6 0 3 3 0 016 0z',
        'M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z',
    ],
    plus: 'M12 4v16m8-8H4',
};

function Icon({ name, className = 'w-4 h-4', strokeWidth = 2 }) {
    const paths = [].concat(ICON_PATHS[name] || []);
    return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            {paths.map((d) => (
                <path key={d} strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d={d} />
            ))}
        </svg>
    );
}

const SPINNER_PATHS = {
    short: 'M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z',
    full: 'M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z',
};

function Spinner({ variant = 'short', className = 'h-4 w-4' }) {
    return (
        <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" aria-hidden="true">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d={SPINNER_PATHS[variant]} />
        </svg>
    );
}

// ═════════════════════════════════════════════════════════════
// SMALL PRESENTATIONAL COMPONENTS
// ═════════════════════════════════════════════════════════════
const STAT_CARD_STYLES = {
    highlight: 'bg-white text-gray-800',
    green: 'bg-green-500/20 backdrop-blur-sm border border-green-400/30 text-white',
    red: 'bg-red-500/20 backdrop-blur-sm border border-red-400/30 text-white',
    default: 'bg-white/10 backdrop-blur-sm border border-white/20 text-white',
};

function StatCard({ icon, label, value, highlight, accent }) {
    const bg = highlight
        ? STAT_CARD_STYLES.highlight
        : STAT_CARD_STYLES[accent] || STAT_CARD_STYLES.default;
    return (
        <div className={`${bg} rounded-2xl px-5 py-3 min-w-[110px] flex items-center gap-3 shadow-lg`}>
            <span className="text-2xl" aria-hidden="true">{icon}</span>
            <div>
                <p className={`text-xs font-medium uppercase tracking-wide ${highlight ? 'text-gray-500' : 'text-white/70'}`}>
                    {label}
                </p>
                <p className={`text-2xl font-extrabold leading-tight ${highlight ? 'text-gray-800' : 'text-white'}`}>
                    {value}
                </p>
            </div>
        </div>
    );
}

function MoneyStat({ label, value, alignRight }) {
    return (
        <div className={alignRight ? 'text-right' : undefined}>
            <p className="text-white/60 text-xs uppercase tracking-wide font-medium">{label}</p>
            <p className="text-white text-2xl sm:text-3xl font-extrabold tracking-tight">{formatMoney(value)}</p>
        </div>
    );
}

const FILTER_ACTIVE_STYLES = {
    green: 'bg-green-600 text-white shadow-md shadow-green-600/25',
    red: 'bg-red-600 text-white shadow-md shadow-red-600/25',
    default: 'bg-[#3a663f] text-white shadow-md shadow-[#3a663f]/25',
};

function FilterPill({ label, count, active, onClick, accent }) {
    const classes = active
        ? FILTER_ACTIVE_STYLES[accent] || FILTER_ACTIVE_STYLES.default
        : 'text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700';
    return (
        <button
            type="button"
            onClick={onClick}
            aria-pressed={active}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${classes}`}
        >
            <span>{label}</span>
            <span
                className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${active
                    ? 'bg-white/25 text-white'
                    : 'bg-gray-200 dark:bg-slate-700 text-gray-500 dark:text-slate-400'
                    }`}
            >
                {count}
            </span>
        </button>
    );
}

function ExportMenuItem({ icon, title, count, onClick }) {
    return (
        <button
            type="button"
            role="menuitem"
            onClick={onClick}
            className="w-full flex items-start gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-slate-700/60 transition-colors text-left"
        >
            <Icon name={icon} className="w-5 h-5 mt-0.5 text-[#3a663f] dark:text-green-400 flex-shrink-0" />
            <div className="flex-1">
                <p className="text-sm font-bold text-gray-800 dark:text-white">{title}</p>
                <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">{plural(count, 'certificate')}</p>
            </div>
        </button>
    );
}

const TOAST_STYLES = {
    success: {
        wrap: 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20',
        icon: 'checkCircle',
        iconColor: 'text-green-500',
        text: 'text-green-700 dark:text-green-300',
    },
    error: {
        wrap: 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20',
        icon: 'alertCircle',
        iconColor: 'text-red-500',
        text: 'text-red-700 dark:text-red-300',
    },
};

function Toast({ variant, message, onDismiss }) {
    const s = TOAST_STYLES[variant];
    return (
        <div
            role={variant === 'error' ? 'alert' : 'status'}
            className={`rounded-2xl border-2 ${s.wrap} px-4 py-3 flex items-start gap-3 animate-slideDown shadow-lg`}
        >
            <Icon name={s.icon} className={`w-5 h-5 flex-shrink-0 mt-0.5 ${s.iconColor}`} />
            <p className={`text-sm flex-1 ${s.text}`}>{message}</p>
            {onDismiss && (
                <button type="button" onClick={onDismiss} aria-label="Dismiss" className="text-red-400 hover:text-red-600">
                    <Icon name="close" className="w-4 h-4" />
                </button>
            )}
        </div>
    );
}

function LoadingSkeleton() {
    return (
        <div className="space-y-4" aria-busy="true" aria-label="Loading certificates">
            {[0, 1, 2].map((i) => (
                <div key={i} className="bg-white dark:bg-slate-800 rounded-3xl border-2 border-gray-100 dark:border-slate-700 p-6 animate-pulse">
                    <div className="flex items-start justify-between gap-4 mb-4">
                        <div className="flex items-center gap-3 flex-1">
                            <div className="w-12 h-12 rounded-2xl bg-gray-200 dark:bg-slate-700" />
                            <div className="flex-1 space-y-2">
                                <div className="h-5 w-40 bg-gray-200 dark:bg-slate-700 rounded" />
                                <div className="h-3 w-32 bg-gray-100 dark:bg-slate-700/60 rounded" />
                            </div>
                        </div>
                        <div className="h-6 w-20 bg-gray-200 dark:bg-slate-700 rounded-full" />
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                        {[0, 1, 2, 3].map((j) => (
                            <div key={j} className="h-10 bg-gray-100 dark:bg-slate-700/60 rounded-xl" />
                        ))}
                    </div>
                    <div className="h-12 bg-gray-100 dark:bg-slate-700/60 rounded-2xl" />
                </div>
            ))}
        </div>
    );
}

function ErrorState({ message, onRetry }) {
    return (
        <div role="alert" className="bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-800 rounded-3xl p-8 text-center shadow-lg">
            <div className="w-16 h-16 mx-auto rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-4">
                <Icon name="alertCircle" className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="text-lg font-bold text-red-800 dark:text-red-400 mb-1">Could Not Load</h3>
            <p className="text-sm text-red-600 dark:text-red-300 mb-5">{message}</p>
            <button
                type="button"
                onClick={onRetry}
                className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-bold py-2.5 px-5 rounded-xl transition-colors shadow-md"
            >
                Try Again
            </button>
        </div>
    );
}

// ═════════════════════════════════════════════════════════════
// CERTIFICATE ROW
// ═════════════════════════════════════════════════════════════
function CertificateRow({
    cert, isPrivileged,
    selected, onToggleSelect,
    onDownload, downloading,
    onView, onCopy, copied,
}) {
    const isCancelled = isCancelledCert(cert);
    const issuedOn = issuedAt(cert);
    const checkboxLabel = `Select certificate ${cert.certificate_no}`;

    const borderClass = selected
        ? 'border-[#3a663f] dark:border-green-400 shadow-lg shadow-[#3a663f]/10'
        : isCancelled
            ? 'border-red-100 dark:border-red-900/40 hover:border-red-200 dark:hover:border-red-800'
            : 'border-gray-100 dark:border-slate-700 hover:border-[#3a663f]/30';

    return (
        <div className={`group relative bg-white dark:bg-slate-800 rounded-3xl border-2 overflow-hidden transition-all hover:shadow-2xl hover:-translate-y-0.5 ${borderClass}`}>
            <div className="flex flex-col lg:flex-row">
                {/* Selection rail (desktop) */}
                <div className="hidden lg:flex flex-col items-center pt-6 pl-4">
                    <input
                        type="checkbox"
                        checked={selected}
                        onChange={onToggleSelect}
                        aria-label={checkboxLabel}
                        className={`w-5 h-5 ${CHECKBOX} cursor-pointer`}
                    />
                </div>

                {/* Status rail */}
                <div
                    className={`w-full lg:w-1.5 h-1.5 lg:h-auto flex-shrink-0 ${isCancelled ? 'bg-red-500' : 'bg-gradient-to-b from-[#2d5232] to-[#3a663f]'}`}
                />

                <div className="flex-1 p-5 lg:p-6">
                    {/* Header */}
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
                        <div className="flex items-center gap-3 min-w-0">
                            {/* Selection (mobile) */}
                            <input
                                type="checkbox"
                                checked={selected}
                                onChange={onToggleSelect}
                                aria-label={checkboxLabel}
                                className={`lg:hidden w-4 h-4 ${CHECKBOX}`}
                            />
                            <div
                                className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-sm ${isCancelled ? 'bg-red-50 dark:bg-red-900/30' : 'bg-[#3a663f]/10 dark:bg-[#3a663f]/20'}`}
                            >
                                {isCancelled
                                    ? <Icon name="close" className="w-6 h-6 text-red-500" />
                                    : <Icon name="checkCircle" className="w-6 h-6 text-[#3a663f] dark:text-green-400" />}
                            </div>
                            <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                    <p className="text-lg font-extrabold text-gray-800 dark:text-white break-words tracking-tight">
                                        {cert.certificate_no || '—'}
                                    </p>
                                    {cert.certificate_no && (
                                        <button
                                            type="button"
                                            onClick={onCopy}
                                            aria-label="Copy certificate number"
                                            title="Copy certificate number"
                                            className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity p-1 rounded-lg text-gray-400 hover:text-[#3a663f] dark:hover:text-green-400 hover:bg-gray-100 dark:hover:bg-slate-700"
                                        >
                                            {copied
                                                ? <Icon name="check" className="w-3.5 h-3.5 text-green-500" strokeWidth={2.5} />
                                                : <Icon name="copy" className="w-3.5 h-3.5" />}
                                        </button>
                                    )}
                                </div>
                                <p className="text-xs text-gray-400 dark:text-slate-500">
                                    {isCancelled ? 'Cancelled Certificate' : 'Active Certificate'}
                                </p>
                            </div>
                        </div>

                        <StatusBadge isCancelled={isCancelled} />
                    </div>

                    {/* Info grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-3 mb-3">
                        <MiniInfo icon="🆔" label="IDF" value={cert.idf_number} />
                        <MiniInfo icon="📋" label="Quote" value={cert.quote_number} />
                        <MiniInfo icon="💰" label="Insured" value={formatMoney(cert.insured_amount)} />
                        {cert.transaction_no
                            ? <MiniInfo icon="🔗" label="DMVIC Ref" value={cert.transaction_no} />
                            : <MiniInfo icon="🏢" label="Insurer" value="Mayfair Insurance" />}
                        {isPrivileged && (
                            <MiniInfo icon="👤" label="Owner" value={cert.owner_name || cert.owner_email || '—'} />
                        )}
                    </div>

                    {/* Policy number */}
                    <div className="mb-4">
                        <div className="flex items-center gap-2 p-3 rounded-2xl border-2 border-dashed border-[#3a663f]/25 dark:border-green-400/25 bg-[#3a663f]/[0.03] dark:bg-green-400/[0.05]">
                            <Icon name="tag" className="w-4 h-4 text-[#3a663f] dark:text-green-400 flex-shrink-0" />
                            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-slate-500">
                                Policy Number
                            </p>
                            <p
                                className={`ml-auto text-sm font-mono font-bold truncate ${cert.ira_policy_num
                                    ? 'text-[#3a663f] dark:text-green-400'
                                    : 'text-gray-400 dark:text-slate-500 italic font-normal'
                                    }`}
                                title={cert.ira_policy_num || 'Not set'}
                            >
                                {cert.ira_policy_num || 'Not set'}
                            </p>
                        </div>
                    </div>

                    {/* Audit trail */}
                    <div className="bg-gray-50 dark:bg-slate-900/50 rounded-2xl p-4 mb-4 space-y-3">
                        <AuditEvent
                            variant="issued"
                            title="Issued"
                            when={issuedOn}
                            by={cert.issued_by_name}
                        />
                        {isCancelled && (
                            <AuditEvent
                                variant="cancelled"
                                title="Cancelled"
                                when={cert.cancelled_at}
                                by={cert.cancelled_by_name}
                                note={cert.cancel_reason}
                            />
                        )}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col sm:flex-row gap-2">
                        <button
                            type="button"
                            onClick={onDownload}
                            disabled={downloading}
                            aria-busy={downloading}
                            className={`flex-1 inline-flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-sm font-bold transition-all shadow-sm ${isCancelled
                                ? 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-600'
                                : `${PRIMARY_GRADIENT} shadow-md shadow-[#3a663f]/20 hover:shadow-lg`
                                } disabled:opacity-60 disabled:cursor-not-allowed`}
                        >
                            {downloading ? (
                                <>
                                    <Spinner variant="full" />
                                    Downloading...
                                </>
                            ) : (
                                <>
                                    <Icon name="fileDownload" className="w-4 h-4" />
                                    Download
                                </>
                            )}
                        </button>
                        <button
                            type="button"
                            onClick={onView}
                            className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-sm font-bold bg-white dark:bg-slate-700 border-2 border-gray-200 dark:border-slate-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-600 transition-all"
                        >
                            <Icon name="eye" className="w-4 h-4" />
                            View Details
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function StatusBadge({ isCancelled }) {
    return (
        <span
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide self-start ${isCancelled
                ? 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                : 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                }`}
        >
            <Icon name={isCancelled ? 'close' : 'check'} className="w-3 h-3" strokeWidth={2.5} />
            {isCancelled ? 'Cancelled' : 'Active'}
        </span>
    );
}

function AuditEvent({ variant, title, when, by, note }) {
    const isIssued = variant === 'issued';
    return (
        <div className="flex items-start gap-3">
            <div className={`w-7 h-7 rounded-full ${isIssued ? 'bg-green-500' : 'bg-red-500'} flex items-center justify-center flex-shrink-0 shadow-sm mt-0.5`}>
                <Icon name={isIssued ? 'check' : 'close'} className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-baseline gap-2">
                    <p className="text-sm font-bold text-gray-800 dark:text-white">{title}</p>
                    <time className="text-xs text-gray-400 dark:text-slate-500" title={formatFullDate(when)}>
                        {formatDate(when)}
                    </time>
                </div>
                <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5 truncate" title={formatFullDate(when)}>
                    By <strong className="text-gray-700 dark:text-slate-300">{by || 'System'}</strong>
                    {note && <span className="text-gray-400 dark:text-slate-500 ml-1 italic">#{note}</span>}
                </p>
            </div>
        </div>
    );
}

function MiniInfo({ icon, label, value }) {
    const display = value || '—';
    return (
        <div>
            <div className="flex items-center gap-1 mb-0.5">
                <span className="text-[10px]" aria-hidden="true">{icon}</span>
                <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-slate-500">{label}</p>
            </div>
            <p className="text-sm font-semibold text-gray-800 dark:text-white truncate" title={display}>
                {display}
            </p>
        </div>
    );
}

// ═════════════════════════════════════════════════════════════
// EMPTY STATE
// ═════════════════════════════════════════════════════════════
function EmptyState({ filter, searchTerm, hasAny, isPrivileged, onClearSearch, onClearFilter, onGoToCargo }) {
    const isFiltered = filter !== 'all' || searchTerm;

    const emptyMessage = hasAny
        ? 'No certificates match your current view.'
        : isPrivileged
            ? 'Once certificates are issued on the platform, they will appear here.'
            : 'Once you generate your first marine cargo certificate, it will appear here.';

    return (
        <div className="bg-white dark:bg-slate-800 rounded-3xl border-2 border-dashed border-gray-200 dark:border-slate-700 p-12 text-center shadow-sm">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#3a663f]/10 dark:bg-[#3a663f]/20 mb-5">
                <Icon name="document" className="w-10 h-10 text-[#3a663f] dark:text-green-400" strokeWidth={1.5} />
            </div>

            {isFiltered ? (
                <>
                    <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-2">No Matching Certificates</h3>
                    <p className="text-sm text-gray-500 dark:text-slate-400 max-w-sm mx-auto mb-5">
                        {searchTerm ? `No certificates match "${searchTerm}".` : 'No certificates match the current filter.'}
                    </p>
                    <div className="flex flex-wrap items-center justify-center gap-2">
                        {searchTerm && (
                            <button type="button" onClick={onClearSearch} className={SECONDARY_BTN}>
                                Clear Search
                            </button>
                        )}
                        {filter !== 'all' && (
                            <button type="button" onClick={onClearFilter} className={SECONDARY_BTN}>
                                Show All
                            </button>
                        )}
                    </div>
                </>
            ) : (
                <>
                    <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-2">
                        {isPrivileged ? 'No Certificates Yet' : 'You Have No Certificates Yet'}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-slate-400 max-w-sm mx-auto mb-5">{emptyMessage}</p>
                    {!isPrivileged && (
                        <button
                            type="button"
                            onClick={onGoToCargo}
                            className={`inline-flex items-center gap-2 px-6 py-3 ${PRIMARY_GRADIENT} font-bold rounded-xl shadow-lg shadow-[#3a663f]/20 hover:shadow-xl transition-all`}
                        >
                            <Icon name="plus" className="w-5 h-5" />
                            Create New Certificate
                        </button>
                    )}
                </>
            )}
        </div>
    );
}
